import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// 401 interceptor → bounce back to /login. Skip if we're already there or
// the call was /auth/me (used by the AuthContext probe).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const path = err?.config?.url || "";
    if (err?.response?.status === 401 && !path.endsWith("/auth/me") && !path.endsWith("/auth/login")) {
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?next=${next}`;
      }
    }
    return Promise.reject(err);
  },
);

export const CATEGORIES = [
  { id: "rodeo", name: "Rodeo" },
  { id: "jackpot", name: "Jackpot" },
  { id: "ranch_rodeo", name: "Ranch Rodeo" },
  { id: "wild_horse_race", name: "Wild Horse Race" },
  { id: "bull_riding", name: "Bull Riding" },
  { id: "junior_steer_riding", name: "Junior Steer Riding" },
  { id: "junior_bull_riding", name: "Junior Bull Riding" },
  { id: "junior_breakaway", name: "Junior Breakaway" },
  { id: "novice_horse_riding", name: "Novice Horse Riding" },
  { id: "novice_saddle_bronc", name: "Novice Saddle Bronc" },
  { id: "saddle_bronc", name: "Saddle Bronc" },
  { id: "barrel_racing", name: "Barrel Racing" },
  { id: "team_roping", name: "Team Roping" },
  { id: "tie_down_roping", name: "Tie-Down Roping" },
  { id: "steer_wrestling", name: "Steer Wrestling" },
  { id: "breakaway", name: "Breakaway" },
  { id: "futurity", name: "Futurity" },
];

export const CATEGORY_NAME = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.name]));

/**
 * Per-event color palette for the Studio timeline.
 * Uses pure Tailwind color values (light + dark tones) so we don't fight the
 * design system. Keep hues visually distinct so admins can eyeball an event
 * transition at a glance.
 *
 *   fill  — solid segment fill on the timeline
 *   ring  — border/ring color used on hover & focus
 *   text  — foreground for chips / tags rendered on top of the segment
 */
export const CATEGORY_COLORS = {
  rodeo:                 { fill: "bg-orange-600/50",  ring: "border-orange-500",  text: "text-orange-200" },
  jackpot:               { fill: "bg-amber-500/50",   ring: "border-amber-400",   text: "text-amber-100" },
  ranch_rodeo:           { fill: "bg-yellow-600/50",  ring: "border-yellow-500",  text: "text-yellow-100" },
  wild_horse_race:       { fill: "bg-red-700/50",     ring: "border-red-500",     text: "text-red-100" },
  bull_riding:           { fill: "bg-rose-700/50",    ring: "border-rose-500",    text: "text-rose-100" },
  junior_steer_riding:   { fill: "bg-pink-600/50",    ring: "border-pink-500",    text: "text-pink-100" },
  junior_bull_riding:    { fill: "bg-fuchsia-700/50", ring: "border-fuchsia-500", text: "text-fuchsia-100" },
  junior_breakaway:      { fill: "bg-purple-600/50",  ring: "border-purple-500",  text: "text-purple-100" },
  novice_horse_riding:   { fill: "bg-violet-600/50",  ring: "border-violet-500",  text: "text-violet-100" },
  novice_saddle_bronc:   { fill: "bg-indigo-600/50",  ring: "border-indigo-500",  text: "text-indigo-100" },
  saddle_bronc:          { fill: "bg-blue-700/50",    ring: "border-blue-500",    text: "text-blue-100" },
  barrel_racing:         { fill: "bg-sky-600/50",     ring: "border-sky-400",     text: "text-sky-100" },
  team_roping:           { fill: "bg-cyan-600/50",    ring: "border-cyan-400",    text: "text-cyan-100" },
  tie_down_roping:       { fill: "bg-teal-600/50",    ring: "border-teal-400",    text: "text-teal-100" },
  steer_wrestling:       { fill: "bg-emerald-700/50", ring: "border-emerald-500", text: "text-emerald-100" },
  breakaway:             { fill: "bg-lime-600/50",    ring: "border-lime-400",    text: "text-lime-100" },
  futurity:              { fill: "bg-green-600/50",    ring: "border-green-400",   text: "text-green-100" },
  // Blooper/reaction/highlight scans land here — hot pink so wrecks pop out on the timeline
  blooper:               { fill: "bg-pink-500/60",    ring: "border-pink-300",    text: "text-pink-50" },
};

/** Fallback swatch when category is unknown or unset. */
export const CATEGORY_COLOR_FALLBACK = {
  fill: "bg-stone-700/60", ring: "border-stone-500", text: "text-stone-100",
};

/** Dead-spot swatch — muted, desaturated so it visually recedes on the timeline. */
export const DEAD_SPOT_COLOR = {
  fill: "bg-stone-800/70", ring: "border-stone-600 border-dashed", text: "text-stone-400",
};

export function categoryColor(catId) {
  return CATEGORY_COLORS[catId] || CATEGORY_COLOR_FALLBACK;
}

export function fmtTime(seconds) {
  if (seconds == null || isNaN(seconds)) return "00:00.00";
  const s = Math.max(0, seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const padMs = sec.toFixed(2).padStart(5, "0");
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${padMs}`;
  return `${m.toString().padStart(2, "0")}:${padMs}`;
}

export function fmtDuration(seconds) {
  if (!seconds) return "0s";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

export function fmtBytes(bytes) {
  if (!bytes) return "0";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
